import * as React from 'react';

import type { TableOfContentsEntry } from '@lexical/react/LexicalTableOfContentsPlugin';
import type { NodeKey } from 'lexical';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { TableOfContentsPlugin as LexicalTableOfContentsPlugin } from '@lexical/react/LexicalTableOfContentsPlugin';
import ArrowUpIcon from '../../../../assets/arrow-up-icon.svg';
import ArrowUpDarkIcon from '../../../../assets/arrow-up-dark.svg';
import CloseIcon from '../../../../assets/close-green-icon.svg';
import TableContentMenuIcon from '../../../../assets/table-contents-menu-icon.svg';
import TwygsBotIcon from '../../../../assets/twygsbot-icon.svg';
import EyeDarkGreenIcon from '../../../../assets/eye-dark-green-icon.svg';
import FactCheckIcon from '../../../../assets/fact-check-icon.svg';
import ArrowLeft from '../../../../assets/arrow-left-white-icon.svg';
import { ZenIcon } from '../../../../assets/zen-icon';
import { TooltipComponent } from '../../../Tooltip';
import DataLayer from '../../../../utils/data-layer';

import './index.css';
import { Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { UnauthorizedDispatcher } from 'src/utils/unauthorized-dispatcher';

export enum InteractionState {
  VISIBLE = 'visible',
  HIDDEN = 'hidden'
}

const getInteractionStateDetails = (state: InteractionState) => {
  switch (state) {
    case InteractionState.VISIBLE:
      return {
        icon: ZenIcon,
        tooltip: 'Enter Zen Mode (Hide Interactions)',
        isZenMode: false
      };
    case InteractionState.HIDDEN:
      return {
        icon: EyeDarkGreenIcon,
        tooltip: 'Exit Zen Mode (Show Interactions)',
        isZenMode: true
      };
    default:
      return {
        icon: ZenIcon,
        tooltip: 'Toggle Zen Mode',
        isZenMode: false
      };
  }
};
const getNextState = (currentState: InteractionState): InteractionState => {
  switch (currentState) {
    case InteractionState.VISIBLE:
      return InteractionState.HIDDEN;
    case InteractionState.HIDDEN:
      return InteractionState.VISIBLE;
    default:
      return InteractionState.VISIBLE;
  }
};

function TableOfContentsPreviewList({
  tableOfContents,
  setIsTwygsBotOpen,
  setIsFactCheckOpen
}: {
  tableOfContents: Array<TableOfContentsEntry>;
  setIsTwygsBotOpen: (value) => {};
  setIsFactCheckOpen: (value) => {};
}): JSX.Element {
  const [selectedKey, setSelectedKey] = React.useState('');
  const [isOpenTablePlugin, setIsOpenTablePlugin] = React.useState(false);
  const [isOpenActionButtonMenu, setIsOpenActionButtonMenu] = React.useState(false);
  const selectedIndex = React.useRef(0);
  const [editor] = useLexicalComposerContext();
  const SECTION_HEADER = ['h1', 'h2', 'h3'];
  const userId = JSON.parse(localStorage.getItem('user'))?.id;
  const [interactionState, setInteractionState] = useState<InteractionState>(
    InteractionState.VISIBLE
  );

  const applyInteractionState = (state: InteractionState) => {
    // Apply to both old button-container and new interaction-container
    const allContainers = document.querySelectorAll('.button-container, .interaction-container');

    allContainers.forEach((container) => {
      const el = container as HTMLElement;

      el.classList.remove('state-visible', 'state-hidden', 'expanded');

      switch (state) {
        case InteractionState.VISIBLE:
          el.classList.add('state-visible');
          break;
        case InteractionState.HIDDEN:
          el.classList.add('state-hidden');
          break;
      }
    });
  };

  function toggleInteractionState() {
    const nextState = getNextState(interactionState);
    setInteractionState(nextState);

    const allContainers = document.querySelectorAll('.button-container, .interaction-container');
    allContainers.forEach((container) => {
      container.classList.remove('state-visible', 'state-hidden');
      container.classList.add(`state-${nextState}`);
    });
  }

  useEffect(() => {
    applyInteractionState(interactionState);
  }, []);

  const { icon: StateIcon, tooltip: stateTooltip } = getInteractionStateDetails(interactionState);

  function scrollToNode(key: NodeKey, currIndex: number) {
    editor.update(() => {
      document
        .querySelectorAll('.TwygsEditorTheme__h1, .TwygsEditorTheme__h2, .TwygsEditorTheme__h3')
        .forEach((el) => {
          el.classList.remove('highlighted-heading');
        });

      const domElement = editor.getElementByKey(key);
      if (domElement !== null) {
        domElement.classList.add('highlighted-heading');
        const headingOffset = 110;
        const topPosition = domElement.getBoundingClientRect().top + window.scrollY - headingOffset;

        window.scrollTo({
          top: topPosition,
          behavior: 'smooth'
        });

        setSelectedKey(key);
        selectedIndex.current = currIndex;
      }
    });
  }

  function scrollToTop() {
    if (window) {
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }
  }

  return (
    <>
      {/* Desktop UI */}
      <div className="fixed top-[86px] md:top-[134px] left-4 md:left-10 z-50 hidden md:block">
        <div className="p-1 md:p-2 bg-growth-green rounded-[80px] transition-transform duration-300 ease-in-out transform hover:scale-105 cursor-pointer">
          <Link to="/" onClick={scrollToTop}>
            <img
              src={ArrowLeft}
              alt="Go Back"
              className="w-[17px] h-[17px] md:w-[20px] md:h-[20px]"
            />
          </Link>
        </div>
        <div className="p-1 md:p-2 md:mt-3 bg-[#C8E6C9] rounded-[80px] transition-transform duration-300 ease-in-out transform hover:scale-105 cursor-pointer">
          <img
            src={TableContentMenuIcon}
            className="w-[17px] h-[17px] md:w-[20px] md:h-[20px]"
            alt="collapse"
            onClick={() => {
              setIsOpenTablePlugin(!isOpenTablePlugin);
            }}
          />
        </div>
        <div className="p-1 md:p-2 md:mt-3 bg-[#C8E6C9] rounded-[80px] transition-transform duration-300 ease-in-out transform hover:scale-105 cursor-pointer">
          <TooltipComponent
            className="w-[17px] md:w-[20px] h-[17px] md:h-[20px]"
            Icon={FactCheckIcon}
            description="Fact Checker"
            distance="0px"
            onClick={() => {
              if (!userId) return UnauthorizedDispatcher('required');
              DataLayer.trackClickFactChecker();
              setIsFactCheckOpen(true);
            }}
          />
        </div>
        <div className="p-1 md:p-2 md:mt-3 bg-[#C8E6C9] rounded-[80px] transition-transform duration-300 ease-in-out transform hover:scale-105 cursor-pointer">
          <TooltipComponent
            className="w-[17px] md:w-[20px] h-[17px] md:h-[20px]"
            Icon={TwygsBotIcon}
            description="Twygsbot"
            distance="0px"
            onClick={() => {
              if (!userId) return UnauthorizedDispatcher('required');
              DataLayer.trackClickTwygsBot();
              setIsTwygsBotOpen(true);
            }}
          />
        </div>
        <div
          className={`p-1 md:p-2 md:mt-3 user-select-none ${interactionState === InteractionState.HIDDEN ? 'bg-growth-green' : 'bg-[#C8E6C9]'} rounded-[80px] transition-all duration-300 ease-in-out transform hover:scale-105 cursor-pointer interaction-state-${interactionState}`}
          onClick={toggleInteractionState}
          title={stateTooltip}
        >
          {StateIcon === ZenIcon ? (
            <ZenIcon className="w-[17px] md:w-[20px] h-[17px] md:h-[20px]" />
          ) : (
            <TooltipComponent
              className="w-[17px] md:w-[20px] h-[17px] md:h-[20px]"
              Icon={StateIcon}
              description={stateTooltip}
              distance="0px"
              onClick={toggleInteractionState}
            />
          )}
        </div>
      </div>
      {isOpenTablePlugin && (
        <div className="viewer-table-of-contents z-[100] left-4 top-[70px] md:left-10 md:top-[174px] !font-ui">
          <div className="flex flex-col gap-4">
            <div className="w-full flex gap-[10px] pb-2 border-b border-earth-brown-50">
              <img
                src={CloseIcon}
                className="cursor-pointer"
                alt=""
                onClick={() => setIsOpenTablePlugin(!isOpenTablePlugin)}
              />
              <p className="font-[800] text-01-jet-black text-[20px]">Contents</p>
            </div>
            <div
              className="flex items-center gap-2 font-[700] text-sm !text-growth-green-500 cursor-pointer hover:opacity-80"
              onClick={scrollToTop}
            >
              Back to top <img src={ArrowUpIcon} alt="" />
            </div>
            <ul className="preview-headings">
              {tableOfContents.map(([key, text, tag], index) => {
                if (!SECTION_HEADER.includes(tag)) {
                  return null;
                }
                return (
                  <div className={`normal-heading-wrapper`} key={key}>
                    <div onClick={() => scrollToNode(key, index)} role="button" tabIndex={0}>
                      {SECTION_HEADER.includes(tag) && (
                        <li
                          className={`normal-heading ${selectedKey === key ? 'selected-heading' : ''}`}
                        >
                          <p
                            className={`${selectedKey === key ? 'selected-heading' : ''} text-sm font-[900] line-clamp-2`}
                          >
                            {text}
                          </p>
                        </li>
                      )}
                    </div>
                  </div>
                );
              })}
            </ul>
          </div>
        </div>
      )}
      {/* Mobile UI */}
      <div className="fixed bottom-6 right-6 z-[1000] block md:hidden">
        {/* Floating Action Button */}
        {!isOpenActionButtonMenu && (
          <button
            aria-label="Open menu"
            onClick={() => setIsOpenActionButtonMenu(true)}
            className="w-12 h-12 rounded-full bg-growth-green shadow-lg flex items-center justify-center cursor-pointer transition-colors hover:bg-green-700"
          >
            {/* Hamburger icon */}
            <svg width="20" height="21" viewBox="0 0 20 21" fill="none">
              <path
                d="M2 16.5C1.71667 16.5 1.47917 16.4042 1.2875 16.2125C1.09583 16.0208 1 15.7833 1 15.5C1 15.2167 1.09583 14.9792 1.2875 14.7875C1.47917 14.5958 1.71667 14.5 2 14.5H18C18.2833 14.5 18.5208 14.5958 18.7125 14.7875C18.9042 14.9792 19 15.2167 19 15.5C19 15.7833 18.9042 16.0208 18.7125 16.2125C18.5208 16.4042 18.2833 16.5 18 16.5H2ZM2 11.5C1.71667 11.5 1.47917 11.4042 1.2875 11.2125C1.09583 11.0208 1 10.7833 1 10.5C1 10.2167 1.09583 9.97917 1.2875 9.7875C1.47917 9.59583 1.71667 9.5 2 9.5H18C18.2833 9.5 18.5208 9.59583 18.7125 9.7875C18.9042 9.97917 19 10.2167 19 10.5C19 10.7833 18.9042 11.0208 18.7125 11.2125C18.5208 11.4042 18.2833 11.5 18 11.5H2ZM2 6.5C1.71667 6.5 1.47917 6.40417 1.2875 6.2125C1.09583 6.02083 1 5.78333 1 5.5C1 5.21667 1.09583 4.97917 1.2875 4.7875C1.47917 4.59583 1.71667 4.5 2 4.5H18C18.2833 4.5 18.5208 4.59583 18.7125 4.7875C18.9042 4.97917 19 5.21667 19 5.5C19 5.78333 18.9042 6.02083 18.7125 6.2125C18.5208 6.40417 18.2833 6.5 18 6.5H2Z"
                fill="white"
              />
            </svg>
          </button>
        )}
        {/* Popup menu */}
        {isOpenActionButtonMenu && (
          <div className="absolute bottom-14 right-0 bg-white rounded-2xl shadow-lg p-4 min-w-[200px] flex flex-col gap-4 border border-[#C8E6C9]">
            <button
              className="flex items-center gap-3"
              onClick={() => {
                setIsOpenTablePlugin(true);
                setIsOpenActionButtonMenu(false);
              }}
            >
              <div className="p-1 bg-[#C8E6C9] rounded-[80px] flex-shrink-0 transition-transform duration-300 ease-in-out transform hover:scale-105 cursor-pointer">
                <img src={TableContentMenuIcon} className="w-4 h-4" alt="" />
              </div>
              <span className="text-sm text-start self-center font-[700] text-growth-green">
                Table of contents
              </span>
            </button>
            <button
              className="flex items-center gap-3"
              onClick={() => {
                if (!userId) return UnauthorizedDispatcher('required');
                setIsFactCheckOpen(true);
                setIsOpenActionButtonMenu(false);
              }}
            >
              <div className="p-1 bg-[#C8E6C9] rounded-[80px] flex-shrink-0 transition-transform duration-300 ease-in-out transform hover:scale-105 cursor-pointer">
                <img src={FactCheckIcon} className="w-4 h-4" alt="" />
              </div>
              <span className="text-sm text-start self-center font-[700] text-growth-green">
                Fact Checker
              </span>
            </button>
            <button
              className="flex items-center gap-3"
              onClick={() => {
                if (!userId) return UnauthorizedDispatcher('required');
                setIsTwygsBotOpen(true);
                setIsOpenActionButtonMenu(false);
              }}
            >
              <div className="p-1 bg-[#C8E6C9] rounded-[80px] flex-shrink-0 transition-transform duration-300 ease-in-out transform hover:scale-105 cursor-pointer">
                <img src={TwygsBotIcon} className="w-4 h-4" alt="" />
              </div>
              <span className="text-sm text-start self-center font-[700] text-growth-green">
                Twygsbot
              </span>
            </button>
            <button
              className="flex items-center gap-3"
              onClick={() => {
                toggleInteractionState();
                setIsOpenActionButtonMenu(false);
              }}
            >
              <div
                className={`p-1 ${interactionState === InteractionState.HIDDEN ? 'bg-growth-green' : 'bg-[#C8E6C9]'} rounded-[80px] flex-shrink-0 transition-all duration-300 ease-in-out transform hover:scale-105 cursor-pointer`}
              >
                {StateIcon === ZenIcon ? (
                  <ZenIcon className="w-4 h-4" />
                ) : (
                  <img src={StateIcon} className="w-4 h-4" alt="" />
                )}
              </div>
              <span className="text-sm text-start self-center font-[700] text-growth-green">
                {interactionState === InteractionState.VISIBLE ? 'Enter Zen Mode' : 'Exit Zen Mode'}
              </span>
            </button>
            <button
              className="flex items-center gap-3"
              onClick={() => {
                scrollToTop();
                setIsOpenActionButtonMenu(false);
              }}
            >
              <div className="p-1 bg-[#C8E6C9] rounded-[80px] flex-shrink-0 transition-transform duration-300 ease-in-out transform hover:scale-105 cursor-pointer">
                <img src={ArrowUpDarkIcon} className="w-4 h-4" alt="" />
              </div>
              <span className="text-sm text-start self-center font-[700] text-growth-green">
                Back to Top
              </span>
            </button>
          </div>
        )}
        {/* Close button */}
        {isOpenActionButtonMenu && (
          <button
            aria-label="Close menu"
            onClick={() => setIsOpenActionButtonMenu(false)}
            className="w-12 h-12 rounded-full bg-green-50 border border-[#C8E6C9] flex items-center justify-center shadow-lg cursor-pointer"
          >
            <svg
              width="20"
              height="21"
              viewBox="0 0 20 21"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M14.1249 6.37528L5.87533 14.6249M5.87533 6.37528L14.1249 14.6249"
                stroke="#4CAF50"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </button>
        )}
      </div>
    </>
  );
}

export default function TableOfContentsViewerPlugin({ setIsTwygsBotOpen, setIsFactCheckOpen }) {
  return (
    <LexicalTableOfContentsPlugin>
      {(tableOfContents) => {
        return (
          <TableOfContentsPreviewList
            tableOfContents={tableOfContents}
            setIsTwygsBotOpen={setIsTwygsBotOpen}
            setIsFactCheckOpen={setIsFactCheckOpen}
          />
        );
      }}
    </LexicalTableOfContentsPlugin>
  );
}
